<template>
    <FilterFlyout
        :name="$t('results.filtering.productType')"
        :isActive="filterActive"
        :isModified="filterModified"
        @save="save"
        @clear="clear"
    >
        <div class="row item-container">
            <div class="col-6" v-for="(offerType, index) in displayOfferTypes" :key="index">
                <button
                    :class="['btn button bordered filter-option-button', { active: itemSelected(offerType.value) }]"
                    @click="toggleItemSelected(offerType.value)"
                >
                    {{ offerType.display }}
                </button>
            </div>
        </div>
    </FilterFlyout>
</template>

<script>
import { useStore, mapGetters } from 'vuex'
import useFilterBase from '@js/composables/filterBase.js'
import { errorNotify } from '@js/utilities/bugsnag.js'

// lodash components
import isEqual from 'lodash/isEqual'
import indexOf from 'lodash/indexOf'
import isEmpty from 'lodash/isEmpty'
import cloneDeep from 'lodash/cloneDeep'
import forEach from 'lodash/forEach'
import find from 'lodash/find'
import camelCase from 'lodash/camelCase'
import split from 'lodash/split'

export default {
    setup() {
        const store = useStore()

        return {
            store,
            ...useFilterBase(store),
        }
    },

    data() {
        return {
            editingItems: [],
            activeItems: [],
        }
    },

    mounted() {
        this.filterState.save = this.save
        this.filterState.clear = this.clear
        this.filterState.filterItem = this.filterItem
    },

    computed: {
        ...mapGetters(['offerResultDataFilters']),
        filterModified() {
            this.filterState.isModified = !isEqual(this.activeItems, this.editingItems)
            return this.filterState.isModified
        },
        filterActive() {
            this.filterState.isActive = !isEmpty(this.activeItems)
            return this.filterState.isActive
        },
        displayOfferTypes() {
            const displayOfferTypes = []
            forEach(this.offerResultDataFilters.offer_types, (offerType, index) => {
                const camelName = camelCase(offerType)
                const displayText = this.$t(`results.offerBlocks.offerTypes.${camelName}`)

                // in case of missing translation for a new tag
                const translatedReplaced = displayText.replace('results.offerBlocks.offerTypes.', '')
                if (displayText !== translatedReplaced) {
                    errorNotify('Missing translation for offer type', 'error', 'Result Page Filter Type', {
                        offerType: offerType,
                    })
                }

                const addItem = {
                    display: translatedReplaced,
                    value: this.getAlignedOfferType(offerType),
                }

                if (!find(displayOfferTypes, addItem)) {
                    displayOfferTypes.push(addItem)
                }
            })

            return displayOfferTypes
        },
    },

    methods: {
        filterItem(offer) {
            return find(this.activeItems, offerType => {
                const productType = offer?.loan_offer?.credit_card?.product_type
                let productTypeFound = false

                if (productType) {
                    if (productType.length > 0 && productType?.includes(',')) {
                        let productTypes = split(productType, ',')

                        forEach(productTypes, (type, index) => {
                            if (this.getAlignedOfferType(type) === offerType) {
                                productTypeFound = true
                                return false
                            }
                        })
                    } else {
                        if (this.getAlignedOfferType(productType) === offerType) {
                            productTypeFound = true
                        }
                    }
                }

                return this.getAlignedOfferType(offer.type) === offerType || productTypeFound
            })
        },
        toggleItemSelected(item) {
            const index = indexOf(this.editingItems, item)
            if (index >= 0) {
                this.editingItems.splice(index, 1)
            } else {
                this.editingItems.push(item)
            }
        },
        itemSelected(item) {
            return indexOf(this.editingItems, item) >= 0
        },
        save() {
            this.activeItems = cloneDeep(this.editingItems)

            this.trackEvent('event', {
                eventCategory: 'filtering',
                eventAction: 'submit',
                eventLabel: 'offer_type',
            })
        },
        clear() {
            this.editingItems = []
            this.save()
        },
        // handle special cases
        getAlignedOfferType(offerType) {
            if (offerType === 'credit card only') {
                offerType = 'credit_card'
            }

            return offerType
        },
    },
}
</script>

<style lang="scss">
@import '~scss/mixins/gutter-size';
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';
@import '~scss/settings/variables';

.item-container {
    @include resize-gutter(20px);
    margin-bottom: -12px;

    .button {
        margin-bottom: 12px;
        padding: 15px 10px;
        line-height: inherit;
    }
}
</style>
